import React from 'react';
import { StandardTableActions, Badge } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { TruncatedText } from '../../components/DataTable/TruncatedText';
import { OfferTemplate } from './useData';
import type { FieldConfig } from '../../components/generic/fieldRenderers';
import type { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { APP_CONFIG } from '../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Offer templates',
    description: 'Reusable terms (intro, delivery, payment, notes) for different offer types',
    permissionModuleKey: 'offers',
    route: {
      list: '/offer-templates',
      new: '/offer-templates/new',
      detail: (id: number) => `/offer-templates/${id}`,
      edit: (id: number) => `/offer-templates/${id}/edit`,
    },
    api: {
      list: '/offer-templates',
      detail: (id: number) => `/offer-templates/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search templates...',
  },
  filterGroups: [{ name: 'basic', label: 'Basic', icon: '🔍', fields: ['name'] }],
  filters: {
    fields: [
      {
        key: 'name',
        label: 'Name',
        placeholder: 'Type to filter...',
        type: 'text' as const,
        getValue: (item: OfferTemplate) => item.name,
        options: [],
      },
    ],
    applyFilter: (items: OfferTemplate[], filters: { name: string[] | string }) => {
      return items.filter((item) => {
        const nameFilter = filters.name;
        if (typeof nameFilter === 'string' && nameFilter.trim() !== '') {
          if (!(item.name || '').toLowerCase().includes(nameFilter.toLowerCase())) return false;
        } else if (Array.isArray(nameFilter) && nameFilter.length > 0) {
          if (!nameFilter.some((f) => (item.name || '').toLowerCase().includes(String(f).toLowerCase()))) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add template',
        showTitle: false,
        variant: 'primary' as const,
        href: '/offer-templates/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: OfferTemplate) => { window.location.href = `/offer-templates/${item.id}`; },
    keyExtractor: (item: OfferTemplate) => item.id,
  },
  emptyStates: {
    noData: 'Create a template to reuse terms across offers.',
    noSearchResults: 'No templates match your search.',
    noFilterResults: 'No templates match the selected filters.',
  },
  delete: {
    title: 'Delete template',
    message: 'Are you sure you want to delete this template?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete template.',
    handler: async (id: number) => {
      const { deleteOfferTemplate } = await import('../../services/mutations');
      await deleteOfferTemplate(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => (dateString ? new Date(dateString).toLocaleDateString() : '-'),
  },
  detail: {
    subtitle: 'Template details',
    notFoundTitle: 'Template not found',
    notFoundMessage: 'This template does not exist.',
    notFoundBackText: 'Back to templates',
    defaultTitle: 'Unnamed template',
    getTitle: (item: OfferTemplate) => item.name || `Template #${item.id}`,
  },
  form: {
    new: { title: 'New offer template', subtitle: 'Add a template', submitText: 'Create template' },
    edit: { title: 'Edit offer template', subtitle: 'Update template', submitText: 'Update template' },
    cancelText: 'Cancel',
    loadingText: 'Loading template...',
    errorMessage: 'Failed to save template.',
    create: async (data: Partial<OfferTemplate>) => {
      const { createOfferTemplate } = await import('../../services/mutations');
      return await createOfferTemplate({
        name: data.name ?? '',
        isDefault: data.isDefault ?? false,
        introText: data.introText ?? undefined,
        deliveryText: data.deliveryText ?? undefined,
        paymentMethodText: data.paymentMethodText ?? undefined,
        notes: data.notes ?? undefined,
      });
    },
    update: async (id: number, data: Partial<OfferTemplate>) => {
      const { updateOfferTemplate } = await import('../../services/mutations');
      return await updateOfferTemplate(id, {
        name: data.name,
        isDefault: data.isDefault ?? undefined,
        introText: data.introText ?? undefined,
        deliveryText: data.deliveryText ?? undefined,
        paymentMethodText: data.paymentMethodText ?? undefined,
        notes: data.notes ?? undefined,
      });
    },
  },
  fields: [
    { name: 'name', label: 'Template name', type: 'text', placeholder: 'e.g. Standard, Service, Product', validation: { required: true } },
    { name: 'isDefault', label: 'Default template', type: 'switch', helperText: 'Only one template can be default. Used when creating new offers.' },
    { name: 'introText', label: 'Intro text', type: 'textarea', placeholder: 'Introduction', rows: 3 },
    { name: 'deliveryText', label: 'Delivery', type: 'text', placeholder: 'e.g. Delivery in 1-3 days' },
    { name: 'paymentMethodText', label: 'Payment method', type: 'text', placeholder: 'Payment method' },
    { name: 'notes', label: 'Notes / bank details', type: 'textarea', placeholder: 'Notes', rows: 4 },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default' as const,
    sections: [
      {
        id: 'general',
        title: 'General',
        icon: '📄',
        fields: ['name', 'isDefault'],
        collapsible: false,
      },
      {
        id: 'terms',
        title: 'Terms',
        icon: '📋',
        fields: ['introText', 'deliveryText', 'paymentMethodText', 'notes'],
        collapsible: false,
        gridCols: 'md:grid-cols-12',
        fieldSpans: { introText: 'md:col-span-12', deliveryText: 'md:col-span-6', paymentMethodText: 'md:col-span-6', notes: 'md:col-span-12' },
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<OfferTemplate>[] => [
  {
    key: 'name',
    header: 'Name',
    sortable: true,
    render: (v, row) => (
      <span className="inline-flex items-center gap-2 min-w-0">
        <span className="truncate">{v || '-'}</span>
        {row.isDefault && (
          <Badge variant="accent" className="shrink-0">
            Default
          </Badge>
        )}
      </span>
    ),
  },
  { key: 'isDefault', header: 'Default', sortable: true, render: (_, row) => (row.isDefault ? 'Yes' : '-') },
  { key: 'introText', header: 'Terms', sortable: false, render: (v) => (v ? <TruncatedText text={v} maxLength={40} showTooltip /> : '-') },
  { key: 'updatedAt', header: 'Updated', sortable: true, render: (v) => formatDate(v ?? undefined) },
];

export const getDetailSections = (item: OfferTemplate, _formatDate: (dateString?: string) => string) => [
  {
    title: 'Actions',
    icon: '📌',
    content: (
      <button
        type="button"
        onClick={() => handleDuplicate(item)}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        title="Create a copy and open in edit"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
        Duplicate template
      </button>
    ),
  },
  {
    title: 'General',
    icon: '📄',
    fields: [{ label: 'Default template', value: item.isDefault ? 'Yes' : 'No' }],
  },
  {
    title: 'Terms',
    icon: '📋',
    fields: [
      { label: 'Intro', value: item.introText || '-' },
      { label: 'Delivery', value: item.deliveryText || '-' },
      { label: 'Payment', value: item.paymentMethodText || '-' },
      { label: 'Notes / Bank', value: item.notes || '-' },
    ],
  },
];

export const getStatusBadges = (_item: OfferTemplate) => null;

export type OfferTemplateFormData = Partial<OfferTemplate>;
export const getInitialFormData = (): OfferTemplateFormData => ({
  name: '',
  isDefault: false,
  introText: '',
  deliveryText: '',
  paymentMethodText: '',
  notes: '',
});
export const mapItemToFormData = (item: OfferTemplate): OfferTemplateFormData => ({
  name: item.name ?? '',
  isDefault: item.isDefault ?? false,
  introText: item.introText ?? '',
  deliveryText: item.deliveryText ?? '',
  paymentMethodText: item.paymentMethodText ?? '',
  notes: item.notes ?? '',
});

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};

const handleDuplicate = async (item: OfferTemplate) => {
  const { createOfferTemplate } = await import('../../services/mutations');
  const created = await createOfferTemplate({
    name: (item.name || 'Template') + ' (copy)',
    isDefault: false,
    introText: item.introText ?? undefined,
    deliveryText: item.deliveryText ?? undefined,
    paymentMethodText: item.paymentMethodText ?? undefined,
    notes: item.notes ?? undefined,
  });
  const newId = (created as { id?: number })?.id;
  if (typeof newId === 'number') {
    window.location.href = moduleConfig.module.route.edit(newId);
  }
};

export const getTableActions = (
  item: OfferTemplate,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

